/**
 * Workspace Overview Tab - 워크스페이스 요약 정보
 * 
 * - 현재 플랜, 구독 상태
 * - 스토리지 사용량
 * - 멤버 수
 * - 워크스페이스 기본 정보
 */

import { Trans, t } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { CreditCard, BarChart3, User, Building2, Calendar, History, Loader2, FileText, Download, CheckCircle, Eye } from 'lucide-react';
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '../../../components/ui/dialog';
import { cn } from '../../../lib/utils';
import { Fire } from '../../../services/FireClient';
import { getAdminFunc, AdminFire } from '../../../services/SuperAdminFirebase';
import { doc, getDoc, collection, getDocs, query, where, addDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { UsageStats, UsageLimit } from '../../../models/UsageStats';
import { IconPlus } from '../../components/FigmaIcons';

interface Props {
    workspaceId: string;
    workspace: {
        id: string;
        name: string;
        memberCount: number;
        storageMB: number;
        tier: string;
        createdAt: Date;
    };
}

interface SubscriptionSummary {
    planId: string;
    status: string;
    startDate: Date | null;
    endDate: Date | null;
    baseAmount: number;
    amount: number;
    currency: string;
    billingCycle: string;
    discount?: {
        type: string;
        value: number;
        reason?: string;
    } | null;
    reason?: string; // 변경 사유
}

interface BillingSummary {
    companyName?: string;
    contactName?: string;
    email?: string;
}

const WorkspaceOverviewTab: React.FC<Props> = ({ workspaceId, workspace }) => {
    const { i18n } = useLingui();
    const [loading, setLoading] = useState(true);
    const [subscription, setSubscription] = useState<SubscriptionSummary | null>(null);
    const [billing, setBilling] = useState<BillingSummary | null>(null);
    const [usageStats, setUsageStats] = useState<UsageStats | null>(null);
    const [usageLimits, setUsageLimits] = useState<UsageLimit | null>(null);
    const [subscriptionCount, setSubscriptionCount] = useState(0);

    // 추가 정보
    const [scheduledSubscription, setScheduledSubscription] = useState<{
        planId: string;
        scheduledFor: Date | null;
        amount: number;
    } | null>(null);
    const [invoiceCount, setInvoiceCount] = useState(0);
    const [documentCount, setDocumentCount] = useState(0);
    const [latestInvoice, setLatestInvoice] = useState<{
        id: string;
        invoiceNumber: string;
        amount: number;
        status: string;
        issuedAt: Date | null;
    } | null>(null);

    // 청구서 액션 상태
    const [invoiceLoading, setInvoiceLoading] = useState(false);
    const [pdfLoading, setPdfLoading] = useState(false);
    const [showInvoiceDetail, setShowInvoiceDetail] = useState(false);
    const [invoiceDetail, setInvoiceDetail] = useState<any>(null);
    const [currentSubscriptionId, setCurrentSubscriptionId] = useState<string | null>(null);

    useEffect(() => {
        loadOverviewData();
    }, [workspaceId]);

    const loadOverviewData = async () => {
        setLoading(true);
        try {
            // 1. 먼저 workspace 문서에서 overview 캐시 확인
            const workspaceDoc = await getDoc(doc(Fire.store, 'workspaces', workspaceId));
            const wsData = workspaceDoc.data();
            const overview = wsData?.overview;

            if (overview?.currentSubscription) {
                // overview 캐시가 있으면 사용 (1번 읽기로 대부분의 데이터)
                setCurrentSubscriptionId(overview.currentSubscription.id || null);
                setSubscription({
                    planId: overview.currentSubscription.planId || 'FREE',
                    status: overview.currentSubscription.status || 'active',
                    startDate: overview.currentSubscription.startDate?.toDate?.() || null,
                    endDate: overview.currentSubscription.endDate?.toDate?.() || null,
                    baseAmount: overview.currentSubscription.discount ?
                        overview.currentSubscription.amount / (1 - (overview.currentSubscription.discount.value / 100)) :
                        overview.currentSubscription.amount || 0,
                    amount: overview.currentSubscription.amount || 0,
                    currency: overview.currentSubscription.currency || 'JPY',
                    billingCycle: 'monthly',
                    discount: overview.currentSubscription.discount || null,
                    reason: overview.currentSubscription.reason || '',
                });

                if (overview.scheduledSubscription) {
                    setScheduledSubscription({
                        planId: overview.scheduledSubscription.planId,
                        scheduledFor: overview.scheduledSubscription.scheduledFor?.toDate?.() || null,
                        amount: overview.scheduledSubscription.amount || 0,
                    });
                }

                setSubscriptionCount(overview.subscriptionCount || 0);
                setInvoiceCount(overview.invoiceCount || 0);
                setDocumentCount((overview.invoiceCount || 0) + (overview.receiptCount || 0));

                if (overview.latestInvoice) {
                    setLatestInvoice({
                        id: overview.latestInvoice.id || '',
                        invoiceNumber: overview.latestInvoice.invoiceNumber,
                        amount: overview.latestInvoice.amount || 0,
                        status: overview.latestInvoice.status,
                        issuedAt: overview.latestInvoice.issuedAt?.toDate?.() || null,
                    });
                }

                // usage는 overview에서 (부분 데이터만 있을 수 있음)
                if (overview.usage) {
                    setUsageStats({
                        storageBytes: overview.usage.storageBytes || 0,
                        monthlyDownloadBytes: overview.usage.monthlyDownloadBytes || 0,
                        monthlyUploadBytes: overview.usage.monthlyUploadBytes || 0,
                        memberCount: overview.usage.memberCount || 0,
                        channelCount: overview.usage.channelCount || 0,
                        botCount: overview.usage.botCount || 0,
                        storageCount: overview.usage.storageCount || 0,
                        messageCount: overview.usage.messageCount || 0,
                        lastUpdatedAt: overview.usage.lastUpdated?.toDate?.() || new Date(),
                        monthlyResetAt: overview.usage.monthlyResetAt?.toDate?.() || null,
                    } as unknown as UsageStats);
                }

            } else {
                // overview 캐시가 없으면 기존 방식으로 로드
                const subsSnap = await getDocs(collection(Fire.store, 'workspaces', workspaceId, 'subscriptions'));
                setSubscriptionCount(subsSnap.size);

                for (const subDoc of subsSnap.docs) {
                    const data = subDoc.data();
                    if (data.status === 'active') {
                        setSubscription({
                            planId: data.planId || 'FREE',
                            status: data.status || 'active',
                            startDate: data.startDate?.toDate?.() || null,
                            endDate: data.endDate?.toDate?.() || null,
                            baseAmount: data.baseAmount || 0,
                            amount: data.amount || 0,
                            currency: data.currency || 'JPY',
                            billingCycle: data.billingCycle || 'monthly',
                            discount: data.discount || null,
                            reason: data.reason || '',
                        });
                    } else if (data.status === 'scheduled') {
                        setScheduledSubscription({
                            planId: data.planId,
                            scheduledFor: data.scheduledFor?.toDate?.() || null,
                            amount: data.amount || 0,
                        });
                    }
                }

                // billing_documents 조회
                const docsSnap = await getDocs(collection(Fire.store, 'workspaces', workspaceId, 'billing_documents'));
                setDocumentCount(docsSnap.size);
                setInvoiceCount(docsSnap.docs.filter(d => d.data().type === 'INVOICE').length);
            }

            // Billing profile은 별도
            const billingDoc = await getDoc(doc(Fire.store, 'workspaces', workspaceId, 'billing', 'profile'));
            if (billingDoc.exists()) {
                const data = billingDoc.data();
                setBilling({
                    companyName: data.companyName,
                    contactName: data.contactName,
                    email: data.email,
                });
            }

            // Usage stats (overview에 없거나 더 정확한 데이터 필요시)
            if (!usageStats) {
                try {
                    const getUsage = getAdminFunc('getWorkspaceUsage');
                    const result = await getUsage({ workspaceId });
                    const data = result.data as { usage: UsageStats; limits: UsageLimit };
                    setUsageStats(data.usage);
                    setUsageLimits(data.limits);
                } catch (e) {
                    console.error('Failed to load usage:', e);
                }
            }

        } catch (e) {
            console.error('Failed to load overview data:', e);
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (date: Date | null) => date ? date.toLocaleDateString('ja-JP') : '-';
    const formatStorage = (mb: number) => mb >= 1000 ? `${(mb / 1000).toFixed(1)} GB` : `${mb} MB`;
    const formatCurrency = (amount: number, currency: string) => {
        return new Intl.NumberFormat('ja-JP', { style: 'currency', currency }).format(amount);
    };

    // 이번 달 청구서 발행
    const handleCreateInvoice = async () => {
        if (!subscription || subscription.amount <= 0) {
            alert(t(i18n)`Cannot issue invoice for free plan.`);
            return;
        }

        setInvoiceLoading(true);
        try {
            const colRef = collection(Fire.store, 'workspaces', workspaceId, 'billing_documents');

            // 이번 달에 이미 유효한 청구서가 있는지 확인
            const thisMonth = new Date();
            thisMonth.setDate(1);
            thisMonth.setHours(0, 0, 0, 0);

            const existingSnap = await getDocs(query(colRef, where('type', '==', 'INVOICE')));
            const validDocs = existingSnap.docs.filter(d => {
                const data = d.data();
                if (data.status === 'canceled') return false;
                const issuedAt = data.issueDate?.toDate?.() || data.createdAt?.toDate?.();
                return issuedAt && issuedAt >= thisMonth;
            });

            if (validDocs.length > 0) {
                const existing = validDocs[0].data();
                alert(t(i18n)`An invoice already exists for this month.\nInvoice #: ${existing.invoiceNumber || validDocs[0].id}`);
                return;
            }

            if (!window.confirm(t(i18n)`Do you want to issue this month's invoice?`)) return;

            const now = new Date();
            const invoiceNumber = `INV-${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}-${Math.floor(Math.random() * 1000).toString().padStart(3, '0')}`;

            // 일본 총액표시: sub.amount는 세금 포함 금액
            const taxRate = 0.1;
            const total = subscription.amount;
            const taxAmount = Math.floor(total - (total / (1 + taxRate)));
            const subtotal = total - taxAmount;

            const docRef = await addDoc(colRef, {
                type: 'INVOICE',
                invoiceNumber,
                workspaceId,
                subscriptionId: currentSubscriptionId,
                subtotal,
                taxRate,
                taxAmount,
                total,
                currency: subscription.currency,
                issuer: {
                    name: 'Sunbi Soft',
                    registrationNumber: 'T0000000000000',
                    address: '東京都',
                    email: 'billing@sunbisoft.com',
                },
                recipient: {},
                items: [{
                    description: `${subscription.planId} プランサブスクリプション`,
                    quantity: 1,
                    unitPrice: subtotal,
                    amount: subtotal,
                }],
                issueDate: serverTimestamp(),
                status: 'issued',
                createdBy: 'super-admin',
                createdAt: serverTimestamp(),
            });

            // 상태 업데이트
            setLatestInvoice({
                id: docRef.id,
                invoiceNumber,
                amount: total,
                status: 'issued',
                issuedAt: now,
            });
            setInvoiceCount(prev => prev + 1);

            alert(t(i18n)`Invoice has been issued.`);
        } catch (e) {
            console.error('Failed to create invoice:', e);
            alert(t(i18n)`Failed to issue invoice.`);
        } finally {
            setInvoiceLoading(false);
        }
    };

    // PDF 다운로드
    const handleDownloadPDF = async (invoiceId: string) => {
        setPdfLoading(true);
        try {
            const generateBillingPDF = AdminFire.getFunc('generateBillingPDF');
            const result = await generateBillingPDF({ workspaceId, documentId: invoiceId });
            const data = result.data as { success: boolean; url?: string };
            if (data.success && data.url) {
                window.open(data.url, '_blank');
            } else {
                throw new Error('PDF generation failed');
            }
        } catch (e) {
            console.error('Failed to generate PDF:', e);
            alert(t(i18n)`Failed to generate PDF`);
        } finally {
            setPdfLoading(false);
        }
    };

    // 입금 확인
    const handleMarkAsPaid = async (invoiceId: string) => {
        if (!window.confirm(t(i18n)`Confirm payment? The payment date will be set to today.`)) return;

        try {
            const docRef = doc(Fire.store, 'workspaces', workspaceId, 'billing_documents', invoiceId);
            await updateDoc(docRef, {
                status: 'paid',
                paidDate: serverTimestamp(),
            });

            // 상태 업데이트
            if (latestInvoice && latestInvoice.id === invoiceId) {
                setLatestInvoice({ ...latestInvoice, status: 'paid' });
            }

            alert(t(i18n)`Payment confirmed.`);
        } catch (e) {
            console.error('Failed to mark as paid:', e);
            alert(t(i18n)`Processing failed.`);
        }
    };

    // 상세 보기
    const handleViewDetail = async (invoiceId: string) => {
        try {
            const docRef = doc(Fire.store, 'workspaces', workspaceId, 'billing_documents', invoiceId);
            const snap = await getDoc(docRef);
            if (snap.exists()) {
                setInvoiceDetail({
                    id: snap.id,
                    ...snap.data(),
                    issueDate: snap.data().issueDate?.toDate?.() || null,
                    paidDate: snap.data().paidDate?.toDate?.() || null,
                });
                setShowInvoiceDetail(true);
            }
        } catch (e) {
            console.error('Failed to load invoice detail:', e);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Main Info Cards */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Current Subscription */}
                <Card>
                    <CardHeader className="pb-3">
                        <div className="flex items-center justify-between">
                            <CardTitle className="text-lg flex items-center gap-2">
                                <CreditCard className="w-5 h-5 text-indigo-500" />
                                <Trans>Current Subscription</Trans>
                            </CardTitle>
                            <Badge className={cn(
                                "text-sm",
                                subscription?.status === 'active' ? 'bg-emerald-100 text-emerald-700' :
                                    subscription?.status === 'past_due' ? 'bg-red-100 text-red-700' :
                                        'bg-slate-100 text-slate-700'
                            )}>
                                {subscription?.status || '-'}
                            </Badge>
                        </div>
                    </CardHeader>
                    <CardContent>
                        <div className="flex items-baseline gap-2 mb-4">
                            <span className="text-3xl font-bold text-indigo-600">
                                {subscription?.planId || workspace.tier || 'FREE'}
                            </span>
                        </div>

                        {/* Pricing Info */}
                        {(subscription?.amount || subscription?.baseAmount) ? (
                            <div className="p-3 bg-slate-50 rounded-lg mb-4">
                                <div className="flex justify-between items-center">
                                    <span className="text-sm text-slate-500">月額</span>
                                    <div className="text-right">
                                        {subscription?.discount && subscription.baseAmount > subscription.amount && (
                                            <span className="text-xs text-slate-400 line-through mr-2">
                                                {formatCurrency(subscription.baseAmount, subscription.currency)}
                                            </span>
                                        )}
                                        <span className="text-lg font-bold text-indigo-600">
                                            {formatCurrency(subscription?.amount || 0, subscription?.currency || 'JPY')}
                                        </span>
                                    </div>
                                </div>
                                {subscription?.discount && (
                                    <div className="mt-2 text-xs text-emerald-600">
                                        {subscription.discount.type === 'percent'
                                            ? `${subscription.discount.value}% OFF`
                                            : `¥${subscription.discount.value.toLocaleString()} OFF`
                                        }
                                        {subscription.discount.reason && (
                                            <span className="text-slate-500"> ({subscription.discount.reason})</span>
                                        )}
                                    </div>
                                )}
                            </div>
                        ) : null}

                        {/* Date Info */}
                        <div className="grid grid-cols-2 gap-3 text-sm">
                            <div className="flex justify-between">
                                <span className="text-slate-500">Start</span>
                                <span className="font-medium">{formatDate(subscription?.startDate || null)}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-slate-500">End</span>
                                <span className="font-medium">
                                    {subscription?.endDate ? formatDate(subscription.endDate) : (
                                        <span className="text-emerald-600">無期限</span>
                                    )}
                                </span>
                            </div>
                        </div>

                        {/* Reason (변경 사유) */}
                        {subscription?.reason && (
                            <div className="mt-3 p-2 bg-slate-50 rounded text-sm">
                                <span className="text-slate-500 font-medium">변경 사유:</span>
                                <span className="text-slate-700 ml-2">{subscription.reason}</span>
                            </div>
                        )}

                        {/* Scheduled Subscription Notice */}
                        {scheduledSubscription && (
                            <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                                <div className="flex items-center gap-2 text-amber-700 text-sm font-medium">
                                    <Calendar className="w-4 h-4" />
                                    예약된 플랜 변경
                                </div>
                                <div className="mt-2 text-sm">
                                    <span className="font-bold text-amber-800">{scheduledSubscription.planId}</span>
                                    <span className="text-amber-600 ml-2">
                                        ({formatDate(scheduledSubscription.scheduledFor)} 적용 예정)
                                    </span>
                                </div>
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Workspace Stats */}
                <Card>
                    <CardHeader className="pb-3">
                        <CardTitle className="text-lg flex items-center gap-2">
                            <BarChart3 className="w-5 h-5 text-emerald-500" />
                            <Trans>Workspace Stats</Trans>
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="p-3 bg-slate-50 rounded-lg">
                                <div className="flex items-center gap-2 text-slate-500 text-xs mb-1">
                                    <User className="w-3.5 h-3.5" />
                                    <span>Members</span>
                                </div>
                                <div className="text-2xl font-bold">{workspace.memberCount}</div>
                            </div>
                            <div className="p-3 bg-slate-50 rounded-lg">
                                <div className="flex items-center gap-2 text-slate-500 text-xs mb-1">
                                    <BarChart3 className="w-3.5 h-3.5" />
                                    <span>Storage</span>
                                </div>
                                <div className="text-xl font-bold">
                                    {usageStats ? formatStorage(Math.round(usageStats.storageBytes / (1024 * 1024))) : '-'}
                                    {usageLimits && usageLimits.storageBytes > 0 && (
                                        <span className="text-sm font-normal text-slate-400 ml-1">
                                            / {formatStorage(Math.round(usageLimits.storageBytes / (1024 * 1024)))}
                                        </span>
                                    )}
                                </div>
                            </div>
                            <div className="p-3 bg-slate-50 rounded-lg">
                                <div className="flex items-center gap-2 text-slate-500 text-xs mb-1">
                                    <History className="w-3.5 h-3.5" />
                                    <span>Subscriptions</span>
                                </div>
                                <div className="text-2xl font-bold">{subscriptionCount}</div>
                            </div>
                            <div className="p-3 bg-slate-50 rounded-lg">
                                <div className="flex items-center gap-2 text-slate-500 text-xs mb-1">
                                    <Calendar className="w-3.5 h-3.5" />
                                    <span>Created</span>
                                </div>
                                <div className="text-sm font-medium">{formatDate(workspace.createdAt)}</div>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Additional Info - 3 columns */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Billing Summary */}
                <Card>
                    <CardHeader className="pb-3">
                        <div className="flex items-center justify-between">
                            <CardTitle className="text-lg flex items-center gap-2">
                                <Building2 className="w-5 h-5 text-blue-500" />
                                <Trans>This month's billing</Trans>
                            </CardTitle>
                            {subscription && subscription.amount > 0 && !latestInvoice && (
                                <Button
                                    size="sm"
                                    onClick={handleCreateInvoice}
                                    disabled={invoiceLoading}
                                >
                                    {invoiceLoading ? (
                                        <Loader2 className="w-4 h-4 animate-spin mr-1" />
                                    ) : (
                                        <IconPlus className="w-4 h-4 mr-1" />
                                    )}
                                    청구서 발행
                                </Button>
                            )}
                        </div>
                    </CardHeader>
                    <CardContent>
                        {/* 최신 청구서 - 있을 경우 */}
                        {latestInvoice ? (
                            <div className="p-4 bg-slate-50 rounded-lg">
                                <div className="flex items-center justify-between mb-3">
                                    <span className="text-sm font-mono text-slate-600">{latestInvoice.invoiceNumber}</span>
                                    <Badge className={cn(
                                        "text-xs",
                                        latestInvoice.status === 'paid' ? 'bg-emerald-100 text-emerald-700' :
                                            latestInvoice.status === 'issued' ? 'bg-amber-100 text-amber-700' :
                                                'bg-slate-100 text-slate-700'
                                    )}>
                                        {latestInvoice.status === 'paid' ? '入金済み' :
                                            latestInvoice.status === 'issued' ? '未入金' : latestInvoice.status}
                                    </Badge>
                                </div>
                                <div className="text-2xl font-bold text-slate-900 mb-2">
                                    {formatCurrency(latestInvoice.amount, 'JPY')}
                                </div>
                                <div className="text-xs text-slate-400 mb-4">
                                    発行日: {formatDate(latestInvoice.issuedAt)}
                                </div>

                                {/* 액션 버튼들 */}
                                <div className="flex flex-wrap gap-2">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => handleViewDetail(latestInvoice.id)}
                                    >
                                        <Eye className="w-4 h-4 mr-1" />
                                        詳細
                                    </Button>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => handleDownloadPDF(latestInvoice.id)}
                                        disabled={pdfLoading}
                                    >
                                        {pdfLoading ? (
                                            <Loader2 className="w-4 h-4 animate-spin mr-1" />
                                        ) : (
                                            <Download className="w-4 h-4 mr-1" />
                                        )}
                                        PDF
                                    </Button>
                                    {latestInvoice.status === 'issued' && (
                                        <Button
                                            variant="default"
                                            size="sm"
                                            className="bg-emerald-600 hover:bg-emerald-700"
                                            onClick={() => handleMarkAsPaid(latestInvoice.id)}
                                        >
                                            <CheckCircle className="w-4 h-4 mr-1" />
                                            入金確認
                                        </Button>
                                    )}
                                </div>
                            </div>
                        ) : (
                            /* 청구서 없을 경우 */
                            <div className="text-center py-6">
                                {subscription && subscription.amount > 0 ? (
                                    <>
                                        <FileText className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                                        <div className="text-slate-500 text-sm mb-3">
                                            이번 달 청구서가 아직 발행되지 않았습니다.
                                        </div>
                                        <Button onClick={handleCreateInvoice} disabled={invoiceLoading}>
                                            {invoiceLoading ? (
                                                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                                            ) : (
                                                <IconPlus className="w-4 h-4 mr-2" />
                                            )}
                                            지금 발행하기
                                        </Button>
                                    </>
                                ) : (
                                    <>
                                        <FileText className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                                        <div className="text-slate-400 text-sm">
                                            무료 플랜은 청구서가 없습니다.
                                        </div>
                                    </>
                                )}
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Documents Summary */}
                <Card>
                    <CardHeader className="pb-3">
                        <div className="flex items-center justify-between">
                            <CardTitle className="text-lg flex items-center gap-2">
                                <FileText className="w-5 h-5 text-purple-500" />
                                <Trans>Documents</Trans>
                            </CardTitle>
                            <Badge className="bg-purple-100 text-purple-700">
                                {documentCount}건
                            </Badge>
                        </div>
                    </CardHeader>
                    <CardContent>
                        {documentCount > 0 ? (
                            <div className="text-sm text-slate-600">
                                등록된 문서가 {documentCount}건 있습니다.
                            </div>
                        ) : (
                            <div className="text-slate-400 text-sm">등록된 문서가 없습니다.</div>
                        )}
                    </CardContent>
                </Card>

                {/* Workspace ID */}
                <Card>
                    <CardHeader className="pb-3">
                        <CardTitle className="text-lg"><Trans>Workspace ID</Trans></CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="font-mono text-sm text-slate-600 bg-slate-50 p-2 rounded break-all">
                            {workspace.id}
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* 청구서 상세 다이얼로그 */}
            <Dialog open={showInvoiceDetail} onOpenChange={setShowInvoiceDetail}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle>請求書詳細</DialogTitle>
                        <DialogDescription>
                            {invoiceDetail?.invoiceNumber}
                        </DialogDescription>
                    </DialogHeader>

                    {invoiceDetail && (
                        <div className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <div className="text-xs text-slate-500">ステータス</div>
                                    <Badge className={cn(
                                        latestInvoice?.status === 'paid' ? 'bg-emerald-100 text-emerald-700' :
                                            latestInvoice?.status === 'issued' ? 'bg-amber-100 text-amber-700' :
                                                'bg-slate-100 text-slate-700'
                                    )}>
                                        {invoiceDetail.status === 'paid' ? '入金済み' :
                                            invoiceDetail.status === 'issued' ? '未入金' : invoiceDetail.status}
                                    </Badge>
                                </div>
                                <div className="text-right">
                                    <div className="text-xs text-slate-500">合計(税込)</div>
                                    <div className="text-2xl font-bold">{formatCurrency(invoiceDetail.total || 0, invoiceDetail.currency || 'JPY')}</div>
                                </div>
                            </div>

                            <div className="p-3 bg-slate-50 rounded-lg space-y-2 text-sm">
                                <div className="flex justify-between">
                                    <span className="text-slate-500">本体価格</span>
                                    <span>{formatCurrency(invoiceDetail.subtotal || 0, invoiceDetail.currency || 'JPY')}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-slate-500">消費税 ({(invoiceDetail.taxRate || 0.1) * 100}%)</span>
                                    <span>{formatCurrency(invoiceDetail.taxAmount || 0, invoiceDetail.currency || 'JPY')}</span>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4 text-sm">
                                <div>
                                    <div className="text-xs text-slate-500">発行日</div>
                                    <div>{formatDate(invoiceDetail.issueDate)}</div>
                                </div>
                                {invoiceDetail.paidDate && (
                                    <div>
                                        <div className="text-xs text-slate-500">入金日</div>
                                        <div>{formatDate(invoiceDetail.paidDate)}</div>
                                    </div>
                                )}
                            </div>

                            <div className="flex gap-2 pt-4 border-t">
                                <Button
                                    variant="outline"
                                    className="flex-1"
                                    onClick={() => handleDownloadPDF(invoiceDetail.id)}
                                    disabled={pdfLoading}
                                >
                                    {pdfLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Download className="w-4 h-4 mr-2" />}
                                    PDF
                                </Button>
                                {invoiceDetail.status === 'issued' && (
                                    <Button
                                        className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                                        onClick={() => {
                                            handleMarkAsPaid(invoiceDetail.id);
                                            setShowInvoiceDetail(false);
                                        }}
                                    >
                                        <CheckCircle className="w-4 h-4 mr-2" />
                                        入金確認
                                    </Button>
                                )}
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default WorkspaceOverviewTab;
